import { useTranslations } from "@shared/context/useTranslations";
import { useAddress } from "@shared/context/AddressContext";
import { useState, useEffect, forwardRef, useImperativeHandle, useRef } from "react";
import EmptyState from "@shared/components/Atoms/EmptyState/EmptyState.component";
import { AddressCard } from "../AddressCard/AddressCard.component";
import { cn } from "@shared/helpers/classNames.helper";
import { AddressEdit, AddressEditRef, UserAddressState } from "@shared/components/Molecules/AddressEdit/AddressEdit.component";
import { AddressSearch } from "../AddressSearch/AddressSearch.component";
import S from "./DeliveryAddress.module.scss";
import { UserAddress } from "@shared/types/UserAccountTypes.interface";
import { matchAddress } from "@shared/helpers/apis/addressBookHelper";

interface DeliveryAddressProps {
    onAddressSelected?: (address?: UserAddress) => void;
}

export type DeliveryAddressRef = {
    validate: () => UserAddressState
};

export const DeliveryAddress = forwardRef<DeliveryAddressRef, DeliveryAddressProps>(({onAddressSelected}: DeliveryAddressProps, ref) => {
    DeliveryAddress.displayName = "DeliveryAddress";

    const { getTranslation } = useTranslations();
    const { addresses, blank } = useAddress();
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [isEditMode, setIsEditMode] = useState<boolean>(false);
    const [addressList, setAddressList] = useState<UserAddress[]>(addresses || []);
    const [searchQuery, setSearchQuery] = useState("");
    const [showError, setShowError] = useState<boolean>(false);
    const [defaultCountryId] = useState(blank.countryId);
    const editRef = useRef<AddressEditRef>(null);
    const inputAddressRef = useRef<UserAddress>(blank);

    useEffect(() => {
        const list = addresses?.filter(address => matchAddress(address, searchQuery))
        setAddressList(list || [])
    }, [addresses, searchQuery]);

    useEffect(() => {
        const foundAddress = addresses?.filter(item => item.id === selectedId);
        if (onAddressSelected) {
            onAddressSelected(foundAddress?.length ? foundAddress[0] : undefined);
        }
    }, [addresses, onAddressSelected, selectedId]);

    const handleToggleMode = () => {
        if (!isEditMode) {
            editRef.current?.reset(defaultCountryId);
        }

        setIsEditMode(!isEditMode);
        setSelectedId(null);
        setShowError(false);
    }

    // public methods
    useImperativeHandle(ref, () => ({
        validate: () => {

            if (isEditMode && editRef.current) {

                // Validate custom address entered in checkout
                return editRef.current?.validate();    
            } else {

                // Validate address selected from address book list in checkout
                const foundAddress = selectedId ? addresses?.find(item => item.id === selectedId) || null : null;

                setShowError(selectedId === null);

                return {
                    isValid: foundAddress !== null,
                    address: foundAddress,
                    saveAddress: false
                }
            }
        }
    }));


    return (
        <div className={S.deliveryAddressWrapper}>
            <div className={S.deliveryAddressHeader}>
                <div className={S.boxName}>{getTranslation("deliveryAddress.title")}</div>
                <button className={S.linkButton} onClick={handleToggleMode}>
                    {getTranslation(isEditMode ? "deliveryAddress.backToList": "deliveryAddress.chooseOther")}
                </button>
            </div>

            <div className={cn(S.expandWrapper, isEditMode && S.expanded)}>
                <div className={S.expandInner}> 
                    <AddressEdit
                        ref={editRef}
                        allowSaveAddressOverride={true}
                        address={inputAddressRef.current!}
                    />
                </div>
            </div>

            <div className={cn(S.expandWrapper, !isEditMode && S.expanded)}>
                <div className={S.expandInner}>
                    <div className={S.listHeader}>
                        <h3>{getTranslation("deliveryAddress.addressList")}</h3>
                        <AddressSearch onUpdate={(query: string) => setSearchQuery(query)} />
                    </div>

                    <div className={cn(S.listBox, showError && S.error)}>
                        {addressList && addressList?.length > 0 ? (
                            addressList?.map((address) => (
                                <AddressCard
                                    key={address.id}
                                    address={address}
                                    isSelected={address.id === selectedId}
                                    onSelect={(addressId: string) => setSelectedId(addressId)}
                                />
                            ))
                        ) : (
                            <EmptyState heading={getTranslation("AddressList.noItemsAvalible")} />
                        )}
                    </div>  
                    <div className={cn(S.errorMessage, showError && S.active)}>
                        {getTranslation("AddressList.errorNoItemSelected")}
                    </div>
                </div>
            </div>
        </div>
    );
})
